import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LogOut, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { OmMark } from "@/components/Ornaments";

function defaultTargetDate() {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", hour12: false,
  });
  const parts = Object.fromEntries(fmt.formatToParts(new Date()).map((p) => [p.type, p.value]));
  const istHour = Number(parts.hour);
  const todayIst = `${parts.year}-${parts.month}-${parts.day}`;
  if (istHour < 12) return { dateStr: todayIst, isTomorrow: false };
  const [y, m, d] = todayIst.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d) + 86400000);
  const dateStr = `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, "0")}-${String(t.getUTCDate()).padStart(2, "0")}`;
  return { dateStr, isTomorrow: true };
}

export default function HelperDashboard() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [{ dateStr, isTomorrow }] = useState(defaultTargetDate);
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get(`/bookings/counts?date=${dateStr}`);
      setData(data);
    } finally {
      setRefreshing(false);
    }
  }, [dateStr]);

  useEffect(() => { load(); }, [load]);

  const friendly = new Date(`${dateStr}T00:00:00+05:30`).toLocaleDateString("en-IN", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });
  const dayLabel = isTomorrow ? t("admin.tomorrow") : t("admin.today");

  return (
    <div className="min-h-screen bg-paper flex flex-col" data-testid="helper-dashboard">
      <header className="border-b border-[color:var(--border)] bg-white">
        <div className="container-x flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <OmMark size={32} className="text-[color:var(--brand)]" />
            <div className="leading-tight">
              <div className="font-heading text-lg">Acharya Guruji</div>
              <div className="text-[10px] uppercase tracking-[0.2em] opacity-70">{t("admin.helperConsole")}</div>
            </div>
          </div>
          <button onClick={logout} data-testid="helper-logout" className="inline-flex items-center gap-1 text-sm border border-[color:var(--border)] rounded-md px-3 py-1.5">
            <LogOut size={14} /> {t("admin.logout")}
          </button>
        </div>
      </header>

      <main className="flex-1 container-x py-8 md:py-12">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl">{dayLabel} · {t("admin.poojaCounts")}</h1>
            <p className="text-sm opacity-65 mt-1">{friendly}</p>
            {user?.name && <p className="text-xs opacity-50 mt-0.5">{user.name}</p>}
          </div>
          <button
            onClick={load}
            disabled={refreshing}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full btn-brand text-sm disabled:opacity-60"
            data-testid="helper-refresh"
          >
            <RotateCcw size={14} className={refreshing ? "animate-spin" : ""} /> {t("admin.refresh")}
          </button>
        </div>

        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data?.by_pooja?.length === 0 && (
            <div className="col-span-full text-center py-16 opacity-65" data-testid="helper-empty">
              {t("admin.noBookings")}
            </div>
          )}
          {data?.by_pooja?.map((row) => (
            <div key={row.pooja_slug} className="bg-white rounded-md border border-[color:var(--border)] p-6 flex items-center justify-between" data-testid={`helper-row-${row.pooja_slug}`}>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] opacity-60">{t("admin.pooja")}</div>
                <div className="font-heading text-2xl md:text-3xl mt-1 leading-tight">{row.pooja_name}</div>
              </div>
              <div className="text-right">
                <div className="font-heading font-bold text-5xl md:text-6xl text-[color:var(--brand)] leading-none" data-testid={`helper-count-${row.pooja_slug}`}>{row.count}</div>
              </div>
            </div>
          ))}
        </div>

        {data && (
          <div className="mt-8 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[color:var(--bg-2)] border border-[color:var(--border)] text-sm">
            <span className="opacity-65">{t("admin.totalToday")}:</span>
            <span className="font-heading font-bold text-lg text-[color:var(--brand)]" data-testid="helper-total">{data.total}</span>
          </div>
        )}
      </main>
    </div>
  );
}
